import React, { useState, useEffect } from 'react';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { triggerHaptic, HapticPatterns } from '../utils/haptics';
import './PWAInstallBanner.css';

/**
 * Banner that prompts users to install the PWA
 * Shows at the bottom of the screen on mobile devices
 */
const PWAInstallBanner = () => {
  const { isInstallable, isInstalled, promptInstall, dismissPrompt } = usePWAInstall();
  const [isVisible, setIsVisible] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    // Show banner after a short delay (not immediately on page load)
    if (isInstallable && !isInstalled) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 3000); // Show after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [isInstallable, isInstalled]);

  const handleInstall = async () => {
    triggerHaptic(HapticPatterns.medium);
    setIsInstalling(true);

    const accepted = await promptInstall();

    if (accepted) {
      triggerHaptic(HapticPatterns.success);
      setIsVisible(false);
    } else {
      triggerHaptic(HapticPatterns.light);
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    triggerHaptic(HapticPatterns.light);
    setIsVisible(false);
    dismissPrompt();
  };

  if (!isVisible || !isInstallable || isInstalled) {
    return null;
  }

  return (
    <div className="pwa-install-banner">
      <div className="pwa-install-content">
        <div className="pwa-install-icon">
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M19 9H15V3H9V9H5L12 16L19 9ZM5 18V20H19V18H5Z"
              fill="currentColor"
            />
          </svg>
        </div>

        <div className="pwa-install-text">
          <h3 className="pwa-install-title">Install Mandrillapp</h3>
          <p className="pwa-install-description">
            Add to your home screen for quick access and offline functionality
          </p>
        </div>

        <div className="pwa-install-actions">
          <button
            className="pwa-install-btn-primary"
            onClick={handleInstall}
            disabled={isInstalling}
          >
            {isInstalling ? 'Installing...' : 'Install'}
          </button>
          <button
            className="pwa-install-btn-dismiss"
            onClick={handleDismiss}
            disabled={isInstalling}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallBanner;
